import React, { useState, useEffect } from "react";
import { DataTable, Badge, Button, Pagination } from "@/components/ui";
import { fmt } from "@/lib/formatters";
import { 
  Eye, Edit, RefreshCcw, RotateCcw, 
  Check, IndianRupee, Plus, FileText 
} from "lucide-react";

/**
 * UnifiedReturnsTable component
 * Refactored to use config-driven DataTable and reusable UI components.
 */
export const UnifiedReturnsTable = ({ 
  returns, 
  subTab, 
  onView, 
  onEdit, 
  onViewPayment, 
  onConfirm 
}) => {
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setPage(1);
  }, [returns]);

  const rows = returns || [];
  const isMaster = subTab === "master";
  const columns = [
    {
      header: isMaster ? "Master Returns" : "Return ID",
      render: (r) => (
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded bg-slate-50 flex items-center justify-center border border-slate-100 ${
            r.rawType === 'purchase' ? 'text-emerald-400' : 'text-blue-400'
          }`}>
            {r.rawType === 'purchase' ? <RefreshCcw size={14} /> : <RotateCcw size={14} />}
          </div>
          <span className="text-[12px] font-normal text-slate-700 font-['Poppins']">{r.returnId}</span>
        </div>
      )
    },
    {
      header: isMaster ? "Return Type" : (subTab === "site" ? "Status (Reason)" : "Status / Reason"),
      render: (r) => isMaster ? (
        <Badge 
          variant={r.rawType === 'purchase' ? 'info' : 'warning'}
          label={r.returnType === 'Purchase' || r.returnType === 'Purchase Return' ? 'Purchase Return' : 'Site Return'} 
        />
      ) : (
        <span className="text-[11px] font-normal text-slate-500 italic">{r.reason}</span>
      )
    },
    {
      header: isMaster ? "Return Reason" : (subTab === "site" ? "Return Date" : "Date"),
      render: (r) => isMaster ? (
        <span className="text-slate-600 text-[11px] font-normal">{r.reason || r.remarks || r.description || "General Return"}</span>
      ) : (
        <span className="text-[11px] font-normal text-slate-500 font-['Poppins']">{r.date ? new Date(r.date).toLocaleDateString() : "—"}</span>
      )
    },
    ...(isMaster ? [{
      header: "Return Date",
      render: (r) => (
        <span className="text-[11px] font-normal text-slate-500 font-['Poppins']">{r.date ? new Date(r.date).toLocaleDateString() : "—"}</span>
      )
    }] : []),
    {
      header: "Items",
      render: (r) => (
        <div className="flex flex-col">
          <span className="text-[12px] font-normal text-slate-700 font-['Poppins'] truncate max-w-[180px]">{r.itemSummary || "No Items"}</span>
          <span className="text-[10px] text-slate-400 font-normal uppercase tracking-tight">
            {r.rawType === 'purchase' ? `Vendor: ${r.party}` : `Site: ${r.party}`}
          </span>
        </div>
      )
    },
    {
      header: "Return Amount",
      render: (r) => <span className="text-[12px] font-normal text-[#f97316] font-['Poppins']">₹{fmt(r.amount)}</span>
    }
  ];

  if (!isMaster) {
    if (subTab === "purchases") {
      columns.push({
        header: "Received",
        render: (r) => <span className="text-[12px] font-normal text-emerald-600 font-['Poppins']">{fmt(r.receivedAmount || 0)}</span>
      });
    } else if (subTab === "sales") {
      columns.push({
        header: "Invoice #",
        render: (r) => (
          <div className="flex items-center gap-1.5 text-[10px] font-normal uppercase tracking-tight text-[#2b448f] bg-blue-50 w-fit px-2 py-0.5 rounded border border-blue-100">
            {r.invoiceNo}
          </div>
        )
      });
    } else if (subTab === "site") {
      const amountIndex = columns.findIndex(c => c.header === "Return Amount");
      const insertedCols = [
        {
          header: "Item Qty",
          render: (r) => {
            let totalQty = 0;
            if (Array.isArray(r.items)) {
              totalQty = r.items.reduce((sum, it) => sum + (Number(it.quantity || it.returnedQuantity) || 0), 0);
            } else if (r.quantity || r.returnedQuantity) {
              totalQty = Number(r.quantity || r.returnedQuantity) || 0;
            }
            return <span className="text-[11px] font-normal text-slate-700 font-['Poppins']">{totalQty} {r.items?.[0]?.unit || 'NOS'}</span>
          }
        },
        {
          header: "Return Type",
          render: (r) => <Badge variant="warning" label="Site Return" />
        },
        {
          header: "Doc Type",
          render: (r) => (
            <span className="text-[11px] font-normal text-slate-600 font-['Poppins']">
              {r.docType || "—"}
            </span>
          )
        },
        {
          header: "Sale Type",
          render: (r) => (
            <span className="text-[11px] font-normal text-slate-600 font-['Poppins']">
              {r.saleType || "—"}
            </span>
          )
        }
      ];
      columns.splice(amountIndex > -1 ? amountIndex : columns.length, 0, ...insertedCols);

      columns.push({
        header: "Project ID",
        render: (r) => (
          <div className="flex items-center gap-1.5 text-[10px] font-normal uppercase tracking-tight text-[#f97316] bg-blue-50 w-fit px-2 py-0.5 rounded border border-blue-100">
            {r.projectId || r.displayProjectId || r.invoiceNo || "N/A"}
          </div>
        )
      });
    }
  }

  columns.push({
    header: "Action",
    render: (r) => (
      <div className="flex gap-1.5 items-center">
        <Button variant="ghost" size="icon-xs" onClick={() => onView(r)} title="View Details">
          <Eye size={14} />
        </Button>

        {!isMaster && subTab !== "purchases" && subTab !== "site" && (
          <Button variant="ghost" size="icon-xs" onClick={() => onEdit(r)} title="Edit Return">
            <Edit size={14} />
          </Button>
        )}

        {/* Site returns: show + Restock when PENDING or RECEIVED */}
        {!isMaster && r.rawType === 'site' && (() => {
          const s = (r.status || '').toLowerCase();
          return (s === 'pending' || s === 'received') && s !== 'restocked';
        })() && (
          <Button
            variant="ghost"
            size="icon-xs"
            className="text-blue-500 hover:text-blue-600 hover:bg-blue-50"
            onClick={() => onConfirm(r, 'restock')}
            title="Confirm & Restock to Inventory"
          >
            <Plus size={14} />
          </Button>
        )}

        {!isMaster && r.rawType === 'purchase' && (Math.round(Number(r.receivedAmount || 0)) < Math.round(Number(r.amount || 0))) && (
          <Button
            variant="ghost"
            size="icon-xs"
            className="text-blue-500 hover:text-blue-600 hover:bg-blue-50"
            onClick={() => onViewPayment(r)}
            title="Record Refund Payout"
          >
            <IndianRupee size={14} />
          </Button>
        )}

        {!isMaster && r.rawType === 'sale' && (() => {
          const s = (r.status || '').toLowerCase();
          return s !== 'received' && s !== 'restocked' && s !== 'owner_approved';
        })() && (
          <Button
            variant="ghost"
            size="icon-xs"
            className="text-blue-500 hover:text-blue-600 hover:bg-blue-50"
            onClick={() => window.confirm("Confirm receipt of returned material?") && onConfirm(r, 'receive')}
            title="Confirm Receipt"
          >
            <Check size={14} />
          </Button>
        )}

        {!isMaster && r.rawType === 'sale' && (() => {
          const s = (r.status || '').toLowerCase();
          return (s === 'received' || s === 'owner_approved') && s !== 'restocked';
        })() && (
          <Button
            variant="ghost"
            size="icon-xs"
            className="text-blue-500 hover:text-blue-600 hover:bg-blue-50"
            onClick={() => onConfirm(r, 'restock')}
            title="Add to Inventory"
          >
            <Plus size={14} />
          </Button>
        )}
      </div>
    )
  });

  const currentData = rows.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const totalPages = Math.max(Math.ceil(rows.length / itemsPerPage), 1);

  return (
    <div className="card rounded-none overflow-hidden border-slate-100 shadow-sm">
      <DataTable columns={columns} data={currentData} />
      
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />

      {rows.length === 0 && (
        <div className="p-12 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
          <p className="text-slate-400 font-medium">No return records found</p>
        </div>
      )}
    </div>
  );
};
